import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bot, Play, Pause, Settings, Workflow, MessageSquare, Clock, Zap, AlertCircle } from 'lucide-react';
import { PageTransition } from '@/components/motion';
import Card from '@/components/UI/Card';
import Badge from '@/components/UI/Badge';
import BrokerSettingsModal from '@/components/Apollo/BrokerSettingsModal';
import { brokersAPI, actionsAPI } from '@/services/apolloApi';

const STATUS_CONFIG = {
  idle: { variant: 'neutral', label: 'Idle' },
  analyzing: { variant: 'info', label: 'Analyzing' },
  trading: { variant: 'success', label: 'Trading' },
  paused: { variant: 'warning', label: 'Paused' },
  error: { variant: 'danger', label: 'Error' },
};

const AUTONOMY_CONFIG = {
  observe: { label: 'Observe Only', description: 'Monitors and reports, takes no actions' },
  suggest: { label: 'Suggest', description: 'Proposes actions for your approval' },
  auto: { label: 'Auto-Execute', description: 'Takes actions autonomously within guardrails' },
};

function BrokerDashboard() {
  const { brokerId } = useParams();
  const navigate = useNavigate();
  const [broker, setBroker] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!brokerId) return;
    setLoading(true);
    Promise.all([
      brokersAPI.getById(brokerId),
      actionsAPI.getAll(brokerId, { limit: 10 }),
    ])
      .then(([b, a]) => {
        setBroker(b);
        setActions(a);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [brokerId]);

  async function handleToggleStatus() {
    if (!broker) return;
    const newStatus = broker.status === 'trading' || broker.status === 'analyzing' ? 'paused' : 'trading';
    try {
      const updated = await brokersAPI.updateStatus(broker.id, newStatus);
      setBroker(updated);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  }

  if (loading) {
    return (
      <PageTransition className="p-6">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageTransition>
    );
  }

  if (!broker) {
    return (
      <PageTransition className="p-6">
        <Card>
          <div className="text-center py-16">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Broker not found</p>
          </div>
        </Card>
      </PageTransition>
    );
  }

  const statusCfg = STATUS_CONFIG[broker.status] || STATUS_CONFIG.idle;
  const autonomyCfg = AUTONOMY_CONFIG[broker.autonomy] || AUTONOMY_CONFIG.observe;

  return (
    <PageTransition className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">{broker.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
              <span className="text-xs text-muted-foreground">{autonomyCfg.label}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-accent/10 hover:bg-accent/20 text-foreground transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
          <button
            onClick={() => navigate(`/apollo/chat/${broker.id}`)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-accent/10 hover:bg-accent/20 text-foreground transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Chat
          </button>
          <button
            onClick={() => navigate(`/apollo/workflow/${broker.id}`)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-accent/10 hover:bg-accent/20 text-foreground transition-colors"
          >
            <Workflow className="w-4 h-4" />
            Edit Workflow
          </button>
          <button
            onClick={handleToggleStatus}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
              broker.status === 'trading' || broker.status === 'analyzing'
                ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-400'
                : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400'
            }`}
          >
            {broker.status === 'trading' || broker.status === 'analyzing' ? (
              <><Pause className="w-4 h-4" />Pause</>
            ) : (
              <><Play className="w-4 h-4" />Start</>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Config summary */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configuration
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Autonomy</span>
              <span className="text-foreground">{autonomyCfg.label}</span>
            </div>
            <p className="text-xs text-muted-foreground">{autonomyCfg.description}</p>
            {broker.config?.assetFocus && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Focus</span>
                <span className="text-foreground capitalize">{broker.config.assetFocus}</span>
              </div>
            )}
            {broker.config?.tradingMode && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Mode</span>
                <Badge variant={broker.config.tradingMode === 'live' ? 'danger' : 'info'}>
                  {broker.config.tradingMode}
                </Badge>
              </div>
            )}
          </div>
        </Card>

        {/* Workflow status */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Workflow className="w-4 h-4" />
            Workflow
          </h3>
          {broker.workflow?.nodes?.length > 0 ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nodes</span>
                <span className="text-foreground">{broker.workflow.nodes.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Edges</span>
                <span className="text-foreground">{broker.workflow.edges?.length || 0}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-2">No workflow configured</p>
              <button
                onClick={() => navigate(`/apollo/workflow/${broker.id}`)}
                className="text-xs text-primary hover:underline"
              >
                Set up workflow
              </button>
            </div>
          )}
        </Card>

        {/* Recent activity */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Recent Actions
          </h3>
          {actions.length > 0 ? (
            <div className="space-y-2">
              {actions.slice(0, 5).map((a) => (
                <div key={a.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground truncate">{a.action_type}</span>
                  <Badge variant={a.status === 'executed' ? 'success' : a.status === 'failed' ? 'danger' : 'neutral'}>
                    {a.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No actions yet</p>
          )}
        </Card>
      </div>

      <BrokerSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        broker={broker}
        onSave={async (data) => {
          const updated = await brokersAPI.update(broker.id, data);
          setBroker(updated);
        }}
      />
    </PageTransition>
  );
}

export default BrokerDashboard;
